import { Card, Tag, Divider, Progress } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import tw from "tailwind-styled-components";
import PageTitle from "../page-header/PageHeader";
import Loader from "../loader/loader";

const DetailPIP = () => {
	const { id } = useParams();
	const [pip, setPip] = useState(null);
	const [loading, setLoading] = useState(true);
	const isLogged = Boolean(localStorage.getItem("isLogged"));

	useEffect(() => {
		// Get PIPs from localStorage
		const storedPIPs = localStorage.getItem("performanceImprovementPlans");
		if (storedPIPs) {
			try {
				const pips = JSON.parse(storedPIPs);
				const pipId = parseInt(id);
				const foundPIP = pips.find((p) => p.id === pipId || String(p.id) === String(id));
				if (foundPIP) {
					setPip(foundPIP);
				}
			} catch (error) {
				console.error("Error parsing PIPs from localStorage:", error);
			}
		}
		setLoading(false);
	}, [id]);

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	if (loading) {
		return <Loader />;
	}

	if (!pip) {
		return (
			<div>
				<PageTitle title="Back" />
				<Card className="mt-4">
					<div className="text-center">
						<h3 className="text-xl text-red-500">Performance Improvement Plan not found</h3>
						<p className="text-gray-600 mt-2">The PIP with ID {id} could not be found.</p>
					</div>
				</Card>
			</div>
		);
	}

	const getStatusColor = (status) => {
		switch (status) {
			case "Completed":
				return "green";
			case "Active":
				return "blue";
			case "Under Review":
				return "orange";
			case "Terminated":
				return "red";
			default:
				return "default";
		}
	};

	const calculateProgress = () => {
		if (!pip.startDate || !pip.endDate) return 0;
		const start = dayjs(pip.startDate);
		const end = dayjs(pip.endDate);
		const now = dayjs();
		const totalDays = end.diff(start, "day");
		const elapsedDays = now.diff(start, "day");
		if (totalDays <= 0) return 100;
		const progress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
		return Math.round(progress);
	};

	const getDaysRemaining = () => {
		if (!pip.endDate) return "-";
		const end = dayjs(pip.endDate);
		const now = dayjs();
		const days = end.diff(now, "day");
		if (days < 0) return "Overdue";
		return `${days} days`;
	};

	const progress = calculateProgress();
	const daysRemaining = getDaysRemaining();

	return (
		<div>
			<PageTitle title="Back" />
			<Card className="mt-4">
				<div className="text-center mb-4">
					<h2 className="text-2xl font-semibold text-gray-600">
						Performance Improvement Plan #{pip.id}
					</h2>
					<Tag color={getStatusColor(pip.status)} className="mt-2" style={{ fontSize: 14, padding: "4px 12px" }}>
						{pip.status || "Draft"}
					</Tag>
				</div>
				{pip ? (
					<div className="flex justify-center">
						<div className="w-full max-w-4xl">
							<ul className="list-inside list-none border-2 border-inherit rounded px-5 py-5">
								<ListItem>
									Employee:{" "}
									<TextInside>{pip.employeeName || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Manager:{" "}
									<TextInside>{pip.managerName || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Start Date:{" "}
									<TextInside>
										{pip.startDate
											? dayjs(pip.startDate).format("DD-MM-YYYY")
											: "N/A"}
									</TextInside>
								</ListItem>
								<ListItem>
									End Date:{" "}
									<TextInside>
										{pip.endDate
											? dayjs(pip.endDate).format("DD-MM-YYYY")
											: "N/A"}
									</TextInside>
								</ListItem>
								<ListItem>
									Days Remaining:{" "}
									<TextInside>
										<Tag color={daysRemaining === "Overdue" ? "red" : parseInt(daysRemaining) < 7 ? "orange" : "default"}>
											{daysRemaining}
										</Tag>
									</TextInside>
								</ListItem>
								<ListItem>
									Progress:{" "}
									<TextInside>
										<Progress
											percent={progress}
											status={progress >= 100 ? "success" : "active"}
											strokeColor={progress >= 100 ? "#52c41a" : progress >= 75 ? "#1890ff" : progress >= 50 ? "#faad14" : "#ff4d4f"}
										/>
									</TextInside>
								</ListItem>
								{pip.reviewDate && (
									<ListItem>
										Next Review Date:{" "}
										<TextInside>
											{dayjs(pip.reviewDate).format("DD-MM-YYYY")}
										</TextInside>
									</ListItem>
								)}

								<Divider orientation="left">Performance Issues</Divider>

								<ListItem>
									Performance Concerns:{" "}
									<TextInside>{pip.performanceConcerns || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Root Cause Analysis:{" "}
									<TextInside>{pip.rootCause || "N/A"}</TextInside>
								</ListItem>

								<Divider orientation="left">Improvement Goals</Divider>

								<ListItem>
									Improvement Objectives:{" "}
									<TextInside>{pip.improvementObjectives || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Success Criteria:{" "}
									<TextInside>{pip.successCriteria || "N/A"}</TextInside>
								</ListItem>

								<Divider orientation="left">Action Plan</Divider>

								<ListItem>
									Action Items:{" "}
									<TextInside>{pip.actionItems || "N/A"}</TextInside>
								</ListItem>
								{pip.supportResources && (
									<ListItem>
										Support & Resources:{" "}
										<TextInside>{pip.supportResources}</TextInside>
									</ListItem>
								)}
								{pip.milestones && (
									<ListItem>
										Milestones:{" "}
										<TextInside>{pip.milestones}</TextInside>
									</ListItem>
								)}

								<Divider orientation="left">Comments</Divider>

								{pip.managerComments && (
									<ListItem>
										Manager Comments:{" "}
										<TextInside>{pip.managerComments}</TextInside>
									</ListItem>
								)}
								{pip.employeeComments && (
									<ListItem>
										Employee Comments:{" "}
										<TextInside>{pip.employeeComments}</TextInside>
									</ListItem>
								)}
							</ul>
						</div>
					</div>
				) : (
					<Loader />
				)}
			</Card>
		</div>
	);
};

const ListItem = tw.li`
	text-sm
	text-gray-600
	font-semibold
	py-2
	px-4
	bg-gray-100
	mb-1.5
	rounded
	w-full
	flex
	justify-start
`;

const TextInside = tw.p`
	ml-2
	text-sm
	text-gray-900
	flex-1
`;

export default DetailPIP;

